import { Dispatch } from "react";
import {
  StandardScene,
  type ModelFragmentsPayload,
} from "../../core/standard/standard-scene";
import type {
  MeasureUnits,
  ViewerNavigation,
  ViewerProjection,
  ViewerTool,
} from "../../core/standard/viewer-tools";

export const StandardHandler = {
  viewer: null as StandardScene | null,

  async Start([container, dispatch]: [HTMLDivElement, Dispatch<any>]) {
    if (!this.viewer) {
      this.viewer = new StandardScene(container, dispatch);
    }
  },

  Close() {
    if (this.viewer) {
      this.viewer.Dispose();
      this.viewer = null;
    }
  },

  async HideModelElement() {
    if (this.viewer) {
      await this.viewer.HideModelElement();
    }
  },

  async IsolateModelElement() {
    if (this.viewer) {
      await this.viewer.IsolateModelElement();
    }
  },

  async UnhideAllModelElements() {
    if (this.viewer) {
      await this.viewer.UnhideAllModelElements();
    }
  },

  MountPropertiesPanel(container: HTMLElement) {
    if (this.viewer) {
      this.viewer.MountPropertiesPanel(container);
    }
  },

  UnmountPropertiesPanel() {
    if (this.viewer) {
      this.viewer.UnmountPropertiesPanel();
    }
  },

  MountLayersPanel(container: HTMLElement) {
    if (this.viewer) {
      this.viewer.MountLayersPanel(container);
    }
  },

  UnmountLayersPanel() {
    if (this.viewer) {
      this.viewer.UnmountLayersPanel();
    }
  },

  MountModelTreePanel(container: HTMLElement) {
    if (this.viewer) {
      this.viewer.MountModelTree(container);
    }
  },

  UnmountModelTreePanel() {
    if (this.viewer) {
      this.viewer.UnmountModelTree();
    }
  },

  async SetActiveTool(tool: ViewerTool) {
    if (this.viewer) {
      await this.viewer.SetActiveTool(tool);
    }
  },

  async SetMeasureUnits(units: MeasureUnits) {
    if (this.viewer) {
      await this.viewer.SetMeasureUnits(units);
    }
  },

  async ClearMeasurements() {
    if (this.viewer) {
      await this.viewer.ClearMeasurements();
    }
  },

  async ClearSections() {
    if (this.viewer) {
      await this.viewer.ClearSections();
    }
  },

  async FitModel() {
    if (this.viewer) {
      await this.viewer.FitModel();
    }
  },

  async SetProjection(projection: ViewerProjection) {
    if (this.viewer) {
      await this.viewer.SetProjection(projection);
    }
  },

  async SetNavigation(navigation: ViewerNavigation) {
    if (this.viewer) {
      await this.viewer.SetNavigation(navigation);
    }
  },

  ShowModelElementProperties() {
    if (this.viewer) {
      this.viewer.ShowModelElementProperties();
    }
  },

  async ConvertIfcToFragments(ifcModel: any) {
    if (!this.viewer) {
      throw new Error("Standard viewer is not active!");
    }
    return await this.viewer.ConvertIfcToFragments(ifcModel);
  },

  async OpenModelFragments(payload: ModelFragmentsPayload) {
    if (!this.viewer) {
      throw new Error("Standard viewer is not active!");
    }
    return await this.viewer.OpenModelFragments(payload);
  },
};
